import { trpc } from "../../lib/trpc.js";
import { Lista, Pagina } from "../../components/Pagina.js";
import { Falha, Girando, Selo, Vazio } from "../../components/ui.js";
import { real } from "../../lib/formato.js";

/**
 * Quanto ainda dá para fotografar.
 *
 * O cliente acompanha o consumo em fotos e em reais — o que ele comprou —, não
 * em dólar de IA, que é conta nossa. R$ 250 por 100 fotos são R$ 2,50 cada, e é
 * essa a régua que aparece aqui.
 */
export function PacotePage() {
  const dados = trpc.pacotes.meu.useQuery();

  const ativo = dados.data?.ativo ?? null;
  const historico = dados.data?.historico ?? [];

  return (
    <Pagina
      titulo="Meu pacote"
      descricao="Quantas fotos ainda dá para gerar, e o que já foi usado."
    >
      {dados.isLoading ? (
        <Girando className="mx-auto my-12 size-6 text-texto-fraco" />
      ) : dados.isError ? (
        <Falha mensagem={dados.error.message} />
      ) : !ativo ? (
        <Vazio
          titulo="Sem pacote ativo"
          descricao="Fale com quem administra o sistema para liberar fotos nesta conta."
        />
      ) : (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Indicador
              rotulo="Disponíveis"
              valor={String(ativo.restantes)}
              apoio={`de ${ativo.fotosTotal} do pacote`}
              destaque
            />
            <Indicador
              rotulo="Usadas"
              valor={String(ativo.fotosUsadas)}
              apoio="geradas com sucesso"
            />
            <Indicador
              rotulo="Custo por foto"
              valor={ativo.custoPorFotoCentavos > 0 ? real(ativo.custoPorFotoCentavos / 100) : "—"}
              apoio={ativo.origem === "cortesia" ? "cortesia" : "do valor pago"}
            />
            <Indicador
              rotulo="Já consumido"
              valor={
                ativo.custoPorFotoCentavos > 0
                  ? real((ativo.fotosUsadas * ativo.custoPorFotoCentavos) / 100)
                  : "—"
              }
              apoio="em fotos entregues"
            />
          </div>

          {/* A barra dá a leitura de relance que o número sozinho não dá. */}
          <div>
            <div className="h-2 overflow-hidden rounded-full bg-borda">
              <div
                className="h-full rounded-full bg-brasa transition-[width]"
                style={{
                  width: `${Math.min(100, Math.round((ativo.fotosUsadas / Math.max(1, ativo.fotosTotal)) * 100))}%`,
                }}
              />
            </div>
            <p className="mt-2 text-[0.8125rem] text-texto-fraco">
              Só geração concluída conta. Tentativa que falhou não consome o pacote.
            </p>
          </div>

          {historico.length > 1 && (
            <section>
              <h2 className="mb-2 text-[0.8125rem] font-medium text-texto-suave">Histórico</h2>
              <Lista
                itens={historico}
                chave={(p) => p.id}
                colunas={["Fotos", "Usadas", "Origem", "Situação"]}
                cartao={(p) => (
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[0.9375rem]">
                        {p.fotosUsadas} de {p.fotosTotal}
                      </p>
                      <p className="text-[0.75rem] text-texto-fraco">{p.origem}</p>
                    </div>
                    <Selo tom={p.status === "ativo" ? "fixado" : "quieto"}>{p.status}</Selo>
                  </div>
                )}
                linha={(p) => [
                  <span key="t" className="num">
                    {p.fotosTotal}
                  </span>,
                  <span key="u" className="num">
                    {p.fotosUsadas}
                  </span>,
                  p.origem,
                  <Selo key="s" tom={p.status === "ativo" ? "fixado" : "quieto"}>
                    {p.status}
                  </Selo>,
                ]}
              />
            </section>
          )}
        </div>
      )}
    </Pagina>
  );
}

function Indicador({
  rotulo,
  valor,
  apoio,
  destaque,
}: {
  rotulo: string;
  valor: string;
  apoio?: string;
  destaque?: boolean;
}) {
  return (
    <div className="rounded-peca border border-borda bg-superficie px-4 py-3">
      <p className="text-[0.75rem] uppercase tracking-[0.1em] text-texto-fraco">{rotulo}</p>
      <p
        className={`num mt-1 font-display text-[1.5rem] leading-none ${destaque ? "text-brasa" : ""}`}
      >
        {valor}
      </p>
      {apoio && <p className="mt-1 text-[0.75rem] text-texto-fraco">{apoio}</p>}
    </div>
  );
}
